import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {API_URL} from '../constants';

export interface FinanceBucket {
    key: string;
    label: string;
    count: number;
    gross: number;
    fees: number;
    refunded: number;
    net: number;
}

export interface FinanceSummary {
    currency: string;
    paymentCount: number;
    gross: number;
    fees: number;
    refunded: number;
    net: number;
    paidOut: number;
    awaitingPayout: number;
    byPurpose: FinanceBucket[];
    byMonth: FinanceBucket[];
    byChapter: FinanceBucket[];
    byAccount: FinanceBucket[];
    attention: { [category: string]: number };
}

export interface PaymentRefundDTO {
    refundId?: string;
    amount?: number;
    reason?: string;
    status?: string;
    createdBy?: string;
    createdDate?: string;
    source?: string; // RANA | STRIPE_DASHBOARD
}

/** One ledger row (a Stripe Checkout Session). Amounts are in cents. */
export interface FinancePayment {
    id: string;
    purpose?: string;
    paymentStatus?: string;
    userId?: string;
    chapterId?: string;
    eventId?: string;
    eventTitle?: string;
    bookingId?: string;
    businessConferenceId?: string;
    customerEmail?: string;
    stripeAccountRef?: string;
    stripeSessionId?: string;
    paymentIntentId?: string;
    chargeId?: string;
    amount?: number;
    baseAmount?: number;
    surchargeAmount?: number;
    stripeFee?: number;
    netAmount?: number;
    amountRefunded?: number;
    currency?: string;
    payoutId?: string;
    payoutStatus?: string;
    payoutArrivalDate?: string;
    source?: string;
    createdDate?: string;
    createdEpoch?: number;
    lastSyncedAt?: string;
    effectAppliedAt?: string;
    failureReason?: string;
    reviewStatus?: string;
    livemode?: boolean;
    refunds?: PaymentRefundDTO[];
}

export interface FinanceException {
    category: 'STUCK' | 'EFFECT_PENDING' | 'UNLINKED' | 'DISPUTED' | 'NEEDS_REVIEW';
    severity: 'info' | 'warning' | 'error';
    title: string;
    payment: FinancePayment;
}

export interface AccountBalance {
    accountRef: string;
    usedBy: string[];
    webhookConfigured: boolean;
    available: number | null;
    pending: number | null;
    currency: string;
    error: string | null;
}

export interface PayoutView {
    accountRef: string;
    payoutId: string;
    amount: number;
    currency: string;
    status: string;
    arrivalDate: string | null;
    method: string | null;
    bank: string | null;
    rangePayments: number;
    matchedPayments: number;
    unknownCharges: string[];
}

export interface ImportResult {
    imported: number;
    updated: number;
    skipped: number;
    errors: number;
    nextBefore: number | null;
    done: boolean;
}

export interface FinanceAccount {
    ref: string;
    usedBy: string[];
    webhookConfigured: boolean;
    platform: boolean;
    webhookPath: string;
}

export interface PaymentFilter {
    status?: string;
    purpose?: string;
    chapterId?: string;
    accountRef?: string;
    from?: string;
    to?: string;
    q?: string;
}

@Injectable({providedIn: 'root'})
export class FinanceApiService {

    private apiUrl = `${API_URL}finance`;

    constructor(private http: HttpClient) {
    }

    summary(from?: string, to?: string, chapterId?: string): Observable<FinanceSummary> {
        return this.http.get<FinanceSummary>(`${this.apiUrl}/summary`, {params: this.params({from, to, chapterId})});
    }

    payments(filter: PaymentFilter): Observable<FinancePayment[]> {
        return this.http.get<FinancePayment[]>(`${this.apiUrl}/payments`, {params: this.params({...filter})});
    }

    exceptions(chapterId?: string): Observable<FinanceException[]> {
        return this.http.get<FinanceException[]>(`${this.apiUrl}/exceptions`, {params: this.params({chapterId})});
    }

    syncPayment(id: string): Observable<FinancePayment> {
        return this.http.post<FinancePayment>(`${this.apiUrl}/payments/${id}/sync`, {});
    }

    refund(id: string, amountCents: number | null, reason: string): Observable<FinancePayment> {
        return this.http.post<FinancePayment>(`${this.apiUrl}/payments/${id}/refund`, {amountCents, reason});
    }

    link(id: string, body: { purpose: string; eventId?: string; bookingId?: string; userId?: string; businessConferenceId?: string }): Observable<FinancePayment> {
        return this.http.post<FinancePayment>(`${this.apiUrl}/payments/${id}/link`, body);
    }

    resolveReview(id: string): Observable<FinancePayment> {
        return this.http.put<FinancePayment>(`${this.apiUrl}/payments/${id}/review`, {});
    }

    balances(): Observable<AccountBalance[]> {
        return this.http.get<AccountBalance[]>(`${this.apiUrl}/balances`);
    }

    payouts(days = 90): Observable<PayoutView[]> {
        return this.http.get<PayoutView[]>(`${this.apiUrl}/payouts`, {params: this.params({days: String(days)})});
    }

    importHistory(months: number, before: number | null): Observable<ImportResult> {
        return this.http.post<ImportResult>(`${this.apiUrl}/import`, {months, before});
    }

    sweep(): Observable<{ [k: string]: number }> {
        return this.http.post<{ [k: string]: number }>(`${this.apiUrl}/sweep`, {});
    }

    accounts(): Observable<FinanceAccount[]> {
        return this.http.get<FinanceAccount[]>(`${this.apiUrl}/accounts`);
    }

    saveWebhookSecret(ref: string, secret: string): Observable<{ updatedRows: number }> {
        return this.http.put<{ updatedRows: number }>(`${this.apiUrl}/accounts/${encodeURIComponent(ref)}/webhook-secret`, {secret});
    }

    private params(values: { [key: string]: string | undefined }): HttpParams {
        let p = new HttpParams();
        Object.entries(values).forEach(([k, v]) => {
            if (v !== undefined && v !== null && v !== '') p = p.set(k, v);
        });
        return p;
    }
}
